import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ForumCategoryCard from './components/ForumCategoryCard';
import FeaturedDiscussion from './components/FeaturedDiscussion';
import RecentPostCard from './components/RecentPostCard';
import SearchAndFilters from './components/SearchAndFilters';
import CreatePostModal from './components/CreatePostModal';
import MemberRecognition from './components/MemberRecognition';

const CommunityForums = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ category: '', tier: '' });
  const [sortBy, setSortBy] = useState('recent');

  // Mock user data
  const currentUser = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    tier: "All Access",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
  };

  // Mock notifications
  const notifications = [
    {
      title: "New reply to your post",
      message: "Dr. Martinez replied to your nutrition question",
      time: "5 minutes ago",
      read: false
    },
    {
      title: "Community Challenge Update",
      message: "30-Day Mindfulness Challenge starts tomorrow",
      time: "1 hour ago",
      read: false
    },
    {
      title: "Expert Q&A Session",
      message: "Join live session on sleep optimization",
      time: "2 hours ago",
      read: true
    }
  ];

  // Mock forum categories
  const forumCategories = [
    {
      id: 1,
      name: "Nutrition & Diet",
      description: "Share recipes, meal plans, and nutritional guidance for optimal wellness",
      icon: "Apple",
      color: "bg-green-500",
      postCount: 1247,
      memberCount: 892,
      hasNewPosts: true,
      lastActivity: "2 minutes ago"
    },
    {
      id: 2,
      name: "Fitness & Exercise",
      description: "Workout routines, fitness tips, and exercise motivation from the community",
      icon: "Dumbbell",
      color: "bg-blue-500",
      postCount: 2156,
      memberCount: 1340,
      hasNewPosts: true,
      lastActivity: "8 minutes ago"
    },
    {
      id: 3,
      name: "Mindfulness & Mental Health",
      description: "Meditation practices, stress management, and mental wellness discussions",
      icon: "Brain",
      color: "bg-purple-500",
      postCount: 987,
      memberCount: 756,
      hasNewPosts: false,
      lastActivity: "1 hour ago"
    },
    {
      id: 4,
      name: "Sleep & Recovery",
      description: "Sleep optimization, recovery techniques, and rest-related wellness topics",
      icon: "Moon",
      color: "bg-indigo-500",
      postCount: 634,
      memberCount: 523,
      hasNewPosts: true,
      lastActivity: "15 minutes ago"
    },
    {
      id: 5,
      name: "Center Discussions",
      description: "Connect with members from your local wellness center and nearby locations",
      icon: "MapPin",
      color: "bg-orange-500",
      postCount: 445,
      memberCount: 312,
      hasNewPosts: false,
      lastActivity: "3 hours ago"
    },
    {
      id: 6,
      name: "Community Challenges",
      description: "Join group challenges, competitions, and collaborative wellness goals",
      icon: "Trophy",
      color: "bg-yellow-500",
      postCount: 789,
      memberCount: 1120,
      hasNewPosts: true,
      lastActivity: "5 minutes ago"
    }
  ];

  // Mock featured discussions
  const featuredDiscussions = [
    {
      id: 1,
      title: "How to maintain motivation during winter months?",
      preview: "Looking for strategies to stay active and positive during the colder season...",
      author: {
        name: "Dr. Emily Martinez",
        avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face"
      },
      timeAgo: "2 hours ago",
      likes: 24,
      replies: 18,
      isExpertResponse: true
    },
    {
      id: 2,
      title: "Plant-based protein sources for beginners",
      preview: "Complete guide to incorporating plant proteins into your daily meals...",
      author: {
        name: "Marcus Chen",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
      },
      timeAgo: "4 hours ago",
      likes: 31,
      replies: 12,
      isExpertResponse: false
    },
    {
      id: 3,
      title: "5-minute morning meditation routine",
      preview: "Simple mindfulness practice to start your day with clarity and focus...",
      author: {
        name: "Lisa Thompson",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
      },
      timeAgo: "6 hours ago",
      likes: 45,
      replies: 23,
      isExpertResponse: false
    }
  ];

  // Mock recent posts
  const recentPosts = [
    {
      id: 1,
      title: "My 90-day transformation journey - what I learned",
      content: `After completing my first 90-day wellness program, I wanted to share some insights that might help others starting their journey.\n\nThe biggest lesson was consistency over perfection. I didn't have perfect days, but I showed up every day. Small daily actions compound into significant changes over time.\n\nNutrition was 70% of my success. I focused on whole foods, proper hydration, and meal prep. Exercise was important, but what I ate made the biggest difference in how I felt and looked.`,
      author: {
        name: "Jennifer Walsh",
        avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
        badges: [
          { name: "Transformation Star", color: "bg-yellow-100 text-yellow-800" },
          { name: "All Access", color: "bg-blue-100 text-blue-800" }
        ]
      },
      category: "Fitness & Exercise",
      timeAgo: "3 hours ago",
      likes: 67,
      replies: 24,
      isLiked: false,
      isPinned: true,
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=300&fit=crop",
      lastReply: {
        name: "Dr. Martinez",
        avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face"
      }
    },
    {
      id: 2,
      title: "Question: Best pre-workout snacks for early morning sessions?",
      content: `I've been struggling with energy levels during my 6 AM workouts. I usually don't eat anything beforehand, but I'm wondering if a small snack might help.\n\nWhat do you all eat before early morning workouts? Looking for something light that won't upset my stomach but will give me sustained energy.\n\nCurrently doing a mix of cardio and strength training, sessions last about 45-60 minutes.`,
      author: {
        name: "Mike Rodriguez",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        badges: [
          { name: "Intro Member", color: "bg-green-100 text-green-800" }
        ]
      },
      category: "Nutrition & Diet",
      timeAgo: "5 hours ago",
      likes: 12,
      replies: 8,
      isLiked: true,
      isPinned: false,
      lastReply: {
        name: "Sarah Johnson",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
      }
    },
    {
      id: 3,
      title: "Meditation app recommendations for beginners?",
      content: `I'm new to meditation and feeling a bit overwhelmed by all the options available. I've tried a few different apps but haven't found one that really clicks with me yet.\n\nLooking for something with:\n- Guided sessions for beginners\n- Variety in session lengths (5-20 minutes)\n- Good voice/narration\n- Progress tracking\n\nWhat apps have worked well for you? Any specific programs or teachers you'd recommend?`,
      author: {
        name: "Amanda Foster",
        avatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face",
        badges: [
          { name: "Flex Member", color: "bg-purple-100 text-purple-800" }
        ]
      },
      category: "Mindfulness & Mental Health",
      timeAgo: "8 hours ago",
      likes: 19,
      replies: 15,
      isLiked: false,
      isPinned: false,
      lastReply: {
        name: "Lisa Thompson",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
      }
    }
  ];

  // Mock member recognitions
  const memberRecognitions = [
    {
      member: {
        name: "Jennifer Walsh",
        avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face"
      },
      achievement: "Completed 90-day transformation challenge",
      badge: { name: "Transformation Star", color: "bg-yellow-100 text-yellow-800" },
      icon: "Star",
      points: "500 pts"
    },
    {
      member: {
        name: "Dr. Emily Martinez",
        avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face"
      },
      achievement: "Most helpful community responses this month",
      badge: { name: "Expert Helper", color: "bg-blue-100 text-blue-800" },
      icon: "Award",
      points: "300 pts"
    },
    {
      member: {
        name: "Marcus Chen",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
      },
      achievement: "30-day mindfulness challenge winner",
      badge: { name: "Mindful Master", color: "bg-purple-100 text-purple-800" },
      icon: "Brain",
      points: "250 pts"
    }
  ];

  const handleCategoryClick = (category) => {
    setFilters(prev => ({ ...prev, category: category?.name?.toLowerCase() }));
  };

  const handleDiscussionClick = (discussion) => {
    // Navigate to discussion detail page
    console.log('Navigate to discussion:', discussion?.id);
  };

  const handlePostLike = (postId) => {
    console.log('Like post:', postId);
  };

  const handlePostReply = (postId) => {
    console.log('Reply to post:', postId);
  };

  const handlePostFollow = (postId) => {
    console.log('Follow post:', postId);
  };

  const handleCreatePost = (postData) => {
    console.log('Create new post:', postData);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (filterData) => {
    setFilters(filterData);
  };

  const handleSort = (sortOption) => {
    setSortBy(sortOption);
  };

  const tabs = [
    { id: 'all', label: 'All Discussions', icon: 'MessageSquare' },
    { id: 'following', label: 'Following', icon: 'Bookmark' },
    { id: 'my-posts', label: 'My Posts', icon: 'User' },
    { id: 'questions', label: 'Questions', icon: 'HelpCircle' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={currentUser}
        notifications={notifications}
        onNotificationClick={(notification) => console.log('Notification clicked:', notification)}
        onProfileClick={() => console.log('Profile clicked')}
      />
      <div className="flex">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          user={currentUser}
        />
        
        <main className={`flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } p-6`}>
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
              <div>
                <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
                  Community Forums
                </h1>
                <p className="text-muted-foreground">
                  Connect, share experiences, and learn from our wellness community
                </p>
              </div>
              
              <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                <Button
                  variant="outline"
                  onClick={() => window.location.href = '/events-and-workshops'}
                >
                  <Icon name="Calendar" size={16} />
                  <span className="ml-2">Events & Workshops</span>
                </Button>
                
                <Button
                  onClick={() => setIsCreatePostOpen(true)}
                >
                  <Icon name="Plus" size={16} />
                  <span className="ml-2">New Post</span>
                </Button>
              </div>
            </div>

            {/* Search and Filters */}
            <SearchAndFilters
              onSearch={handleSearch}
              onFilter={handleFilter}
              onSort={handleSort}
            />

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Main Content */}
              <div className="xl:col-span-3 space-y-6">
                {/* Forum Categories */}
                <div>
                  <h2 className="font-heading font-semibold text-xl text-foreground mb-4">
                    Forum Categories
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {forumCategories?.map((category) => (
                      <ForumCategoryCard
                        key={category?.id}
                        category={category}
                        onClick={handleCategoryClick}
                      />
                    ))}
                  </div>
                </div>

                {/* Featured Discussions */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="TrendingUp" size={20} className="text-accent" />
                    <h2 className="font-heading font-semibold text-xl text-foreground">
                      Featured Discussions
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {featuredDiscussions?.map((discussion) => (
                      <FeaturedDiscussion
                        key={discussion?.id}
                        discussion={discussion}
                        onClick={handleDiscussionClick}
                      />
                    ))}
                  </div>
                </div>

                {/* Discussion Tabs */}
                <div className="border-b border-border">
                  <nav className="flex space-x-8">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm transition-wellness ${
                          activeTab === tab?.id
                            ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                        }`}
                      >
                        <Icon name={tab?.icon} size={16} />
                        <span>{tab?.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Recent Posts */}
                <div className="space-y-4">
                  {recentPosts?.map((post) => (
                    <RecentPostCard
                      key={post?.id}
                      post={post}
                      onLike={handlePostLike}
                      onReply={handlePostReply}
                      onFollow={handlePostFollow}
                    />
                  ))}
                </div>

                {/* Load More */}
                <div className="text-center py-6">
                  <Button variant="outline">
                    <Icon name="ChevronDown" size={16} />
                    <span className="ml-2">Load More Discussions</span>
                  </Button>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-semibold text-foreground mb-4">
                    Community Stats
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm">Total Members</span>
                      <span className="font-medium text-foreground">2,847</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm">Active Today</span>
                      <span className="font-medium text-foreground">342</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm">Posts This Week</span>
                      <span className="font-medium text-foreground">156</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-sm">Expert Responses</span>
                      <span className="font-medium text-foreground">23</span>
                    </div>
                  </div>
                </div>

                {/* Member Recognition */}
                <MemberRecognition recognitions={memberRecognitions} />

                {/* Upcoming Events */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="Calendar" size={20} className="text-primary" />
                    <h3 className="font-heading font-semibold text-foreground">Upcoming Events</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-3 bg-primary/5 rounded-lg">
                      <p className="font-medium text-foreground text-sm">Expert Q&A: Sleep Optimization</p>
                      <p className="text-muted-foreground text-xs mt-1">Tomorrow, 7:00 PM EST</p>
                    </div>
                    <div className="p-3 bg-secondary/5 rounded-lg">
                      <p className="font-medium text-foreground text-sm">30-Day Mindfulness Challenge</p>
                      <p className="text-muted-foreground text-xs mt-1">Starts Dec 1, 2024</p>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-4"
                    onClick={() => window.location.href = '/events-and-workshops'}
                  >
                    View All Events
                  </Button>
                </div>

                {/* Community Guidelines */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="Shield" size={20} className="text-success" />
                    <h3 className="font-heading font-semibold text-foreground">Guidelines</h3>
                  </div>
                  
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>• Be respectful and supportive</p>
                    <p>• Share evidence-based information</p>
                    <p>• No medical advice - consult professionals</p>
                    <p>• Keep discussions wellness-focused</p>
                  </div>
                  
                  <Button variant="ghost" size="sm" className="w-full mt-3 text-xs">
                    Read Full Guidelines
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreatePostOpen}
        onClose={() => setIsCreatePostOpen(false)}
        onSubmit={handleCreatePost}
      />
    </div>
  );
};

export default CommunityForums;